<template>
  <div>
    <ToastificationLoadingContent ref="loadingToast" />
    <ToastificationProgressContent ref="progressToast" />

    <Sidenav
      :is-sidebar-active.sync="isSidebarActive"
      :item-edit.sync="itemEdit"
      :is-add.sync="isAdd"
      @refetch-data="refetchData"
    />

    <b-row class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <!-- Filters -->
    <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
      <template v-slot:main-filters>
        <filters
          :tenant-filter.sync="tenantFilter"
          :tenant-options="tenantOptions"
          :server-type-filter.sync="serverTypeFilter"
          :server-type-options="serverTypeOptions"
        />
      </template>
    </filter-modal>

    <!-- Table Container Card -->
    <b-card class="mx-2 mt-1" no-body>
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <PerPageSelector :perPage.sync="perPage" :perPageOptions="perPageOptions" />
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                :placeholder="$t('table.search')"
              />
              <b-button
                variant="primary"
                @click="openAdd()"
                v-if="$can('create', 'AiAnalyticsServers')"
              >
                <span class="text-nowrap text-capitalize">{{
                  `${$t("actions.add")} ${$tc("ai_analytics_servers.server", 1)}`
                }}</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refListTable"
        class="position-relative"
        :items="fetchList"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        :sort-desc.sync="isSortDirDesc"
        show-empty
        :empty-text="$t('no_record_found')"
      >
        <template #head()="data">
          <span> {{ $tc(data.label, 1) }}</span>
        </template>

        <template #cell(restart)="data">
          <b-button
            variant="secondary"
            class="ml-1 btn-sm"
            @click="confirmRestartServer(data.item)"
          >
            <span class="text-nowrap text-capitalize">{{
              $t("ai_analytics_servers.restart_server")
            }}</span>
          </b-button>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-button
            :id="`actions-${data.item.id}`"
            tabindex="0"
            variant="white"
            class="px-1 btn-y"
          >
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="align-middle text-body"
            />
          </b-button>
          <b-popover
            :target="`actions-${data.item.id}`"
            triggers="focus"
            placement="bottom"
            custom-class="popover-p-0"
            variant="white"
          >
            <b-list-group class="m-0">
              <b-list-group-item
                v-if="$can('update', 'AiAnalyticsServers')"
                class="border-0"
                button
                @click="openEdit(data.item)"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("actions.edit") }}
                </span>
              </b-list-group-item>

              <b-list-group-item
                v-if="$can('delete', 'AiAnalyticsServers')"
                class="border-0"
                button
                @click="confirmDelete(data.item.id)"
              >
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("actions.delete") }}
                </span>
              </b-list-group-item>

              <b-list-group-item
                class="border-0"
                button
                @click="getServerDevices(data.item)"
              >
                <feather-icon icon="HardDriveIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("ai_analytics_servers.devices") }}
                </span>
              </b-list-group-item>
            </b-list-group>
          </b-popover>
        </template>

        <template #cell(online)="data">
          <b-badge :variant="data.item.online ? 'success' : 'danger'">
            {{ data.item.online ? "Online" : "Offline" }}
          </b-badge>
        </template>
      </b-table>

      <!-- Footer Table -->
      <TableFooter
        :dataMeta="dataMeta"
        :totalList="totalList"
        :currentPage="currentPage"
        :perPage="perPage"
        @updatePagination="updatePagination"
      />
    </b-card>
  </div>
</template>

<script>
import { getCurrentInstance, ref } from "@vue/composition-api";
import useList from "./useList";
import Sidenav from "./Sidenav.vue";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import ToastificationProgressContent from "@core/components/toastification/ToastificationProgressContent.vue";
import store from "@/store";
import router from '@/router'
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import TableFooter from "@/components/TableFooter.vue";
import PerPageSelector from "@/components/PerPageSelector.vue";
import Filters from "./Filters.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";

export default {
  name: "AiAnalyticsServers",
  components: {
    Sidenav,
    BreadcrumbMenu,
    TableFooter,
    PerPageSelector,
    ToastificationLoadingContent,
    ToastificationProgressContent,
    Filters,
    FilterModal,
    ButtonFilters,
  },
  setup() {
    const vm = getCurrentInstance().proxy;
    const loadingToast = ref(null);
    const isSidebarActive = ref(false);
    const itemEdit = ref({});
    const isAdd = ref(false);

    const openEdit = (item) => {
      isSidebarActive.value = true;
      itemEdit.value = JSON.parse(JSON.stringify(item));
      isAdd.value = false;
    };

    const { t } = useI18nUtils();
    const toast = useToast();

    const trans = {
      "messages.delete.title": t("messages.delete.title"),
      "messages.delete.body": t("messages.delete.body"),
      "actions.delete": t("actions.delete"),
      "actions.cancel": t("actions.cancel"),
      "ai_analytics_servers.uuid_not_found": t("ai_analytics_servers.uuid_not_found"),
      "ai_analytics_servers.restarted_successfully": t("ai_analytics_servers.restarted_successfully"),
      "ai_analytics_servers.restart_error": t("ai_analytics_servers.restart_error"),
      "ai_analytics_servers.fetch_error": t("ai_analytics_servers.fetch_error"),
      "ai_analytics_servers.delete_error": t("ai_analytics_servers.delete_error"),
      "ai_analytics_servers.update_status_error": t("ai_analytics_servers.update_status_error"),
      "ai_analytics_servers.restart_confirmation_title": t("ai_analytics_servers.restart_confirmation_title"),
      "ai_analytics_servers.restart_confirmation_body": t("ai_analytics_servers.restart_confirmation_body"),
      "ai_analytics_servers.restart_server": t("ai_analytics_servers.restart_server"),
    };

    const {
      tableColumns,
      perPage,
      currentPage,
      totalList,
      fetchList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      updatePagination,
      tenantOptions,
      tenantFilter,
      buildFilters,
      clearSearchFilters,
      filtersCount,
      restartServer,
      serverTypeOptions,
      serverTypeFilter,
    } = useList(trans);

    const confirmDelete = (id) => {
      const storeInner = store;
      vm.$swal({
        title: trans["messages.delete.title"],
        text: trans["messages.delete.body"],
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: trans["actions.delete"],
        cancelButtonText: trans["actions.cancel"],
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          storeInner
            .dispatch("ai_analytics_servers/servers/delete", id)
            .then((response) => {
              if (response.success) {
                toast({
                  component: ToastificationContent,
                  props: {
                    title: response.message,
                    icon: "CheckIcon",
                    variant: "success",
                  },
                });
                refetchData();
              } else {
                toast(
                  {
                    component: ToastificationContent,
                    props: {
                      title: response.message,
                      icon: "AlertTriangleIcon",
                      variant: "danger",
                    },
                  },
                  {
                    timeout: 5000,
                  }
                );
              }
            })
            .catch((response) => {
              toast(
                {
                  component: ToastificationContent,
                  props: {
                    title: response.response?.data?.message || "Error al eliminar",
                    icon: "AlertTriangleIcon",
                    variant: "danger",
                  },
                },
                {
                  timeout: 5000,
                }
              );
            });
        }
      });
    };

    const confirmRestartServer = (server) => {
      vm.$swal({
        title: trans["ai_analytics_servers.restart_confirmation_title"],
        text: trans["ai_analytics_servers.restart_confirmation_body"].replace("{serverName}", server.name),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: trans["ai_analytics_servers.restart_server"],
        cancelButtonText: trans["actions.cancel"],
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          restartServer(server.uuid);
        }
      });
    };

    const openAdd = () => {
      isAdd.value = true;
      isSidebarActive.value = true;
    };

    const getServerDevices = (item) => {
      router.push({
        name: 'ai_analytics_devices',
        query: {
          id_server: item.id
        }
      });
    }

    return {
      isSidebarActive,
      isAdd,
      itemEdit,
      openEdit,
      openAdd,
      confirmDelete,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      fetchList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      updatePagination,
      tenantOptions,
      tenantFilter,
      serverTypeOptions,
      serverTypeFilter,
      buildFilters,
      clearSearchFilters,
      filtersCount,
      getServerDevices,
      confirmRestartServer
    };
  },
};
</script>
